<template>
  <div
    class="mine-card reactive"
    :class="[
      { analyzing: isAnalysis },
      { error: isError },
      { 'no-data': isError && !isReportError },
      { 'title-edit-mode': isTitleEditMode },
      { 'is-time-machine': isTimeMachine },
    ]"
    @click="onShowReport"
  >
    <!-- No data, Retry unavailable -->
    <template v-if="isError">
      <div class="error-label error-control">
        <i class="icon icon--bell-till" v-btf-tooltip:top.start="{ contents: $t('mine.report.errorNoData.errorDesc'), offset: -12 }"></i>
        <span>{{ $t('mine.report.errorNoData.errorTag')}}</span>
      </div>
    </template>
    <!-- Data exists, Retry available -->
    <template v-else-if="isReportError">
      <div class="error-label error-control">
        <i class="icon icon--bell-till" v-btf-tooltip:top.start="{ contents: $t('mine.report.errorFail.errorDesc'), trigger: 'click', offset: -12 }"></i>
        <span>{{ $t('mine.report.errorFail.errorTag')}}</span>
      </div>
      <div class="retry-button error-control btf-button primary icon" @click="retryReport">
        <i class="icon icon--refresh-white"></i>
        {{  $t('common.retry') }}
      </div>
    </template>

    <template v-if="isContentCard">
      <div
        class="mine-content mine-card-header"
        :class="[
             `content-vol-${mineData.vol}`,
            `content-${promptType}-${mineData.vol}`,
            `mine-content-${mineData.type.toLowerCase()}`,
            promptType,
            smallTitle
          ]"
        :style="{ backgroundImage: `url(${bgImage})` }"
      >
        <div class="date-view-info">
          <div class="date-info">
              <span class="text">
                {{ setRegDate}}
              </span>
          </div>
          <i class="divider"></i>
          <div class="view-count-info">
            <i class="icon icon--views-content"></i>
            <span class="text">
                {{mineData.viewCount | thousand}}
              </span>
          </div>
        </div>
        <div class="mine-main-info">
          <div class="mine-state" >
            <template v-if="mineData.tagList && mineData.tagList.length > 0">
              <div class="state-label report" v-for="tag in mineData.tagList" v-bind:key="tag">{{ tag }}</div>
            </template>
          </div>
          <div class="mine-title">
            <span class="title-wrap" v-html="mineData.title"></span>
            <p class="prompt-text" v-html="contentText.desc"></p>
          </div>
        </div>
        <div class="mine-bottom-deco">
          <span class="label" v-html="contentText.label"></span>
          <span class="vol" v-html="contentText.vol"></span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="mine-card-header" :class="{'error-line': isError}" >
        <div class="date-view-info">
          <div class="date-info">{{ setRegDate }}</div>
          <i class="divider"></i>
          <div class="view-count-info"><i :class="['icon', isAnalysis ? 'icon--views-disabled' :'icon--views']"></i>{{ mineData.viewCount |thousand }}</div>
        </div>
        <div class="mine-main-info">
          <div class="mine-state">
            <div class="state-label report">{{ reportDisplayMeta.tag }}</div>
            <i v-if="mineData.new" class="icon icon--new-small"></i>
          </div>
          <div class="mine-title">
            <p class="type">{{ reportDisplayMeta.category }}</p>
            <div class="title-wrap-container">
              <div class="title-wrap editable-area" v-if="!isTitleEditMode" @click="startTitleEditing">
                <p class="date-top ellipsis--3">{{ reportTitle }}
                </p>
                <button class="title-edit-button">
                  <i class="icon icon--edit-white-size14"></i>
                </button>
              </div>
              <ui-input-text
                v-else
                class="title-input"
                ref="titleInput"
                :type="'text'"
                :modelValue="newReportTitle"
                :modelError="titleEditError"
                :errorTooltip="titleEditError"
                :placeholder="''"
                :errorType="'iconOnly'"
                :autoFocus="true"
                @input="setReportTitle"
                @onEnter="handleTitleEnter"
                @onBlur="finishTitleEditing"
              />
              <p class="page-name ellipsis--3">
                {{ mineData.pageName }}
              </p>
            </div>
            <div v-if="isAnalysis" class="analyzing-wrap">
              <i class="icon icon--report-bg2"></i>
              <div class="analyzing-text">
                {{ $t('mine.report.analyzing') }}
                <i class="icon icon--analyzing-gray"></i>
              </div>
            </div>
          </div>
        </div>
        <i
          v-if="!isAnalysis"
          class="icon"
          :class="isTimeMachine ? 'icon--report-bg-timemachine' : 'icon--report-bg'"
        ></i>
      </div>
    </template>

  </div>
</template>

<script>
import {
  HEATMAP_REPORT_LINK,
  HEATMAP_REPORT_OLD_LINK,
  REPORT_LINK,
  SOLOMON_REPORT
} from '@/libs/constants'
import { editReportTitle } from '@/api/ai'
import UiInputText from '@/components/ui/UiInputText.vue'
import { isInIframe } from '@/libs/auth-helper'
import { formatToTimeZone } from 'date-fns-timezone'
import { getMineReportDisplayMeta, isTimeMachineReport } from '@/libs/mine-report-helper'

export default {
  name: 'MineListCardItem',
  components: { UiInputText },
  props: {
    mineData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    timezone: {
      type: String,
      default: null
    },
    reportType: {
      type: String,
      default: null
    },
    isError: {
      type: Boolean,
      default: false
    },
    isReportError: {
      type: Boolean,
      default: false
    },
    reportStatus: {
      type: String,
      default: null
    },
    keyword: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isSample: false,
      isReportClicking: false,
      isTitleEditMode: false,
      isComparing: false,
      newReportTitle: '',
      titleEditError: ''
    }
  },
  computed: {
    isContentCard () {
      return this.mineData.type === 'cxTrends'
    },
    bgImage () {
      const { type, vol } = this.mineData

      const fileName =
        this.promptType === 'mini'
          ? `${type}-${this.promptType}-${vol}.png`
          : `${type}-${vol}.png`

      return require(`@/assets/img/mine/${fileName}`)
    },
    promptType () {
      if (!this.mineData || !this.mineData.reportS3Path) return null

      const match = this.mineData.reportS3Path.match(/geo\/([^/]+)/)
      if (!match) return null

      const rawType = match[1]
      const typeMap = {
        'cx-trends-mini': 'mini',
        'cx-trends': 'single'
      }
      return typeMap[rawType] || rawType
    },
    smallTitle () {
      const { vol } = this.mineData

      // cxTrends
      if (this.promptType === 'single' && [5, 7, 8, 9].includes(vol)) {
        return 'small-card-title'
      }

      // cxTrends - mini
      if (this.promptType === 'mini' && [1].includes(vol)) {
        return 'small-card-title'
      }

      return ''
    },
    contentText () {
      if (this.promptType === 'mini') {
        return {
          label: 'CX Trends Benchmark - Big Data',
          desc: '',
          vol: 'Mini Report'
        }
      }

      return {
        label: 'CX Data Trend<br> Research',
        desc: 'CX Trends Benchmark - Big Data',
        vol: `Vol. ${this.mineData.vol}`
      }
    },
    reportDisplayMeta () {
      return getMineReportDisplayMeta(this.mineData)
    },
    isTimeMachine () {
      return isTimeMachineReport(this.mineData)
    },
    reportTitle () {
      return this.mineData.title
    },
    reportLink () {
      if (this.isAnalysis) {
        return null
      } else {
        return REPORT_LINK + '/' + this.mineData.id
      }
    },
    setRegDate () {
      const timeZone = 'Asia/Seoul'

      return formatToTimeZone(
        this.mineData.regDate + 'Z',
        'YYYY-MM-DD HH:mm:ss',
        { timeZone }
      )
    },
    isAnalysis () {
      return this.reportStatus === 'ing'
    }
  },
  watch: {
  },
  deactivated () {
    if (this.isTitleEditMode) {
      this.exitTitleEditing()
    }
  },
  methods: {
    setReportTitle (value) {
      if (value.length > 70) {
        this.titleEditError = this.$t('mine.report.maxLengthError', { limit: 70 })
      } else {
        this.titleEditError = ''
      }
      this.newReportTitle = value
    },
    startTitleEditing () {
      this.newReportTitle = this.reportTitle
      this.isTitleEditMode = true
      this.$emit('changeTitleEditMode', this.mineData.id, true)
    },
    handleTitleEnter () {
      this.$refs.titleInput.$refs.input.blur()
    },
    finishTitleEditing () {
      if (this.titleEditError) return
      if (!this.newReportTitle.trim()) {
        this.exitTitleEditing()
        return
      }
      setTimeout(() => {
        this.isTitleEditMode = false
        this.$emit('changeTitleEditMode', this.mineData.id, false)
      }, 150)
      if (this.reportTitle === this.newReportTitle) return

      editReportTitle({
        reportId: this.mineData.id,
        title: this.newReportTitle
      }).then(res => {
        this.mineData.title = this.newReportTitle
      }).catch(err => {
        console.log(err)
      })
    },
    exitTitleEditing () {
      this.isTitleEditMode = false
      this.titleEditError = ''
      this.newReportTitle = ''
      this.$emit('changeTitleEditMode', this.mineData.id, false)
    },
    async onShowReport () {
      if (this.isAnalysis || this.isError || this.isReportError || this.isComparing || this.isTitleEditMode) {
        return
      }

      const target = isInIframe() ? '_self' : '_blank'
      const id = this.mineData.id || this.mineData._id
      if (!id) return

      this.isReportClicking = true
      this.$emit('onReportLoading', true)

      try {
        if (this.isContentCard) {
          window.open(
            SOLOMON_REPORT + `/${this.mineData.type}/` + id,
            '_blank'
          )
        } else {
          window.open(`${this.mineData.isHtml ? HEATMAP_REPORT_LINK : HEATMAP_REPORT_OLD_LINK}/${id}`, target)
        }
      } catch (err) {
        console.error('getCXReport/createCXReport : ', err)
      } finally {
        this.$emit('onReportLoading', false)
        this.isReportClicking = false
      }
    },
    retryReport () {
      this.$emit('retry', this.mineData)
    }
  }
}
</script>

<style lang="scss" scoped>
$errorColor: #E60724;
$mashupColor: #FF30EA;
$reportColor: #2683CB;
$analyzeColor: #B65EFC;

$reportType: (
    'journey': (#8F74FA, #D3C8FF, #260A93, #6A98FF, #7767FF, #8241FF, url('../../assets/img/mine/mine_journey_bg.svg')),
    'target': (#8F74FA, #D3C8FF, #260A93, #6A98FF, #7767FF, #8241FF, url('../../assets/img/mine/mine_journey_bg.svg')),
    'ranking': (#6289EB, #C9E656, #052472, #33C2FF, #3390DF, #456DD1, url('../../assets/img/mine/mine_ranking_bg.svg')),
    'trend': (#00A5D9, #64DAFF, #005773, #73EAFF, #1DB2E0, #0190D3, url('../../assets/img/mine/mine_trend_bg.svg')),
);

.mine-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  height: 100%;
  background-color: #fff;
  // box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.06), 0px 18px 22px -7px rgba(0, 0, 0, 0.16);

  &.reactive {
    overflow: hidden;
    cursor: pointer;

    &:hover, &.title-edit-mode {
      background-color: #F4F4F4;
    }
    & * {
      transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
    }
    .analyzing-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      margin-top: 8px;
      background: #f4f4f4;

      .icon--report-bg2 {
        mix-blend-mode: luminosity;
      }
      .analyzing-text {
        @include flexXYcenter;
        gap: 12px;
        color: #BBB;
        font-size: 16px;
        font-weight: 600;
        line-height: 150%;

      }
    }
    .icon--report-bg {
      position: absolute;
      bottom: 24px;
      right: 24px;
    }
    /* 타임머신 리포트 전용 일러스트 (Figma: 196x118, right 26 / bottom 40) */
    .icon--report-bg-timemachine {
      position: absolute;
      bottom: 40px;
      right: 26px;
    }
    .mine-card-header {
      position: relative;
      background: none;
      flex: 1;
      &::before {
        display: none;
      }

      .date-view-info {
        color: #444;
        font-size: 12px;
        font-weight: 500;
        line-height: 150%;

        .divider {
          opacity: 0.4;
          display: block;
          width: 1px;
          height: 8px;
          background-color: #bbb;
        }
      }

      .mine-state .state-label {
        border: 1px solid #D7D7D7;
      }

      .mine-card-corner {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 0;
        height: 0;
        border-left: 55px solid transparent;
        border-right: 0 solid transparent;
        border-bottom: 60px solid #fff;

        & .icon {
          position: absolute;
          left: -28px;
          bottom: -51px;
        }
      }

      .mine-main-info {
        position: relative;
        .mine-title {
          margin-top: 26px;
          font-weight: 600;
          text-shadow: none;
          & * {
            transform-origin: top left;
          }
          .type {
            width: 100%;
            font-size: 18px;
            line-height: 1;
            position: relative;
            display: inline-block;
            color: #EC0047;
            margin-bottom: 33px;
            text-transform: capitalize;
            .title-hidden {
              display: none;
            }
            &:after {
             content: '';
              position: absolute;
              left: 0;
              bottom: -17px;
              width: 100%;
              height: 1px;
              background-color: #D9D9D9;
              transition: inherit;
            }
          }
          .title-wrap-container {
            min-height: 112px;
          }
          .date-top {
            color: #444;
            font-size: 26px;
            font-weight: 600;
            line-height: 120%;
            cursor: pointer;

            .date-hidden {
              opacity: 0;
            }
          }
          .date-bottom {
            font-size: 30px;
          }
          .page-name {
            margin-top: 16px;
            color: #666;
            font-size: 14px;
            font-weight: 500;
            line-height: 150%;
          }
          .title-wrap {
            position: relative;
            &:hover {
              .date-top {
                width: 280px;
                transition: none;
                background-color: rgba(0, 0, 0, 0.16);
                box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.16);
                border-radius: 2px;
              }
              .title-edit-button {
                display: flex;
              }
            }
          }
          .title-input {
            margin-top: -4px;
          }
        }
        .month-label {
          position: absolute;
          bottom: 0;
          left: 0;
          text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.20);
          font-size: 68px;
          font-weight: 600;
          line-height: 1;
        }
      }
    }
    &.error {
      cursor: default;
      .mine-card-header {
        padding-top: 13px;
        background: none !important;
        .mine-card-corner {
          display: none;
        }
      }

      &:not(.no-data) {
        .mine-card-header {
          .date-view-info {
            color: #fff;
          }
          .mine-main-info {
            .mine-state > .state-label {
              background-color: #fff !important;
              color: #888;
            }

            .mine-title {
              color: #fff !important;
              margin-top: 15px;
            }
          }
        }
        &:before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: #ebebebc2;
          z-index: 1;
        }
      }

      &.no-data {
        .mine-card-header {
          .mine-main-info {
            .mine-state {
              .state-label {
                background-color: #EBEBEB !important;
                color: #999;
              }
            }
            .mine-title {
              .title {
                display: none;
              }
              .type {
                position: relative;
                font-size: 36px;
                &::after {
                  display: none;
                }
                .title-hidden {
                  display: inline-block;
                  position: absolute;
                  right: -8px;
                  transform: translateX(100%);
                  top: 11px;
                  color: #CCC;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 1.2;
                }
              }
            }
          }
        }
      }
    }

    &.error.no-data, &.analyzing:not(.error) {
      .mine-card-header {
        background: #F4F4F4;
        .mine-main-info {
          position: relative;
          .page-name {
            color: #bbb;
          }
          .mine-state {
            height: 32px;
          }
          .mine-title {
            .type {
              color: #bbb;
            }
            .date-top {
              color: #bbb;
              .date-hidden {
                opacity: 1;
              }
            }
            .date-bottom {
              display: none;
            }
          }
          .month-label {
            position: absolute;
            bottom: 0;
            left: 0;
            text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.20);
            font-size: 68px;
            font-weight: 600;
            line-height: 1;
          }
      }
    }
  }

    &.analyzing {
      cursor: default;
      .mine-card-header .date-view-info {
        color: #bbb;
        opacity: 0.72;
      }
      .mine-state {
        opacity: 0.3;
      }

    }

    &.analyzing:not(.error) {
      .mine-card-header {
        .mine-main-info {
          .mine-state {
            .state-label {
              border: 1px solid rgba(0, 0, 0, 0.12);
            }
          }
          .mine-title {
            .title {
              font-size: 18px;
              font-weight: 500;
              margin-bottom: 4px;
            }
            .type {
              color: #BBB;
            }
          }
        }
        .mine-card-corner {
          display: none;
        }
      }
    }

    &.title-edit-mode {
      cursor: default;
    }

    /* ===== 타임머신 리포트 카드 : 블루 컬러 ===== */
    &.is-time-machine:not(.error):not(.analyzing) {
      .mine-card-header {
        .mine-main-info {
          .mine-title {
            .type {
              color: $timeMachineColor;
            }
          }
        }
        .mine-state {
          .state-label.report {
            color: $timeMachineColor;
          }
        }
      }
    }

    /* ===== 컨텐트 카드 ===== */
    .mine-content {
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center;

      .date-view-info {
        margin-bottom: 0;
      }

      .mine-main-info {
        flex: 0;
        .mine-title {
          display: flex;
          display: flex;
          flex-direction: column;
          margin-top: 0;
          transform: translateY(0)!important;
        }
      }

      .mine-bottom-deco {
        right: 20px;
        bottom: 34px;
        display: flex;
        align-items: center;
        gap: 6px;
        justify-content: flex-start;
        flex-direction: column;
        color: #fff;
        font-weight: 600;
        text-align: center;

        .label {
          font-size: 12px;
          line-height: 120%;
        }
        .vol {
          font-size: 40px;
          line-height: 100%;
        }
      }

      /* 컨텐츠 카드 - cx data */
      &-cxtrends {
        .date-view-info {
          color: #BBB;

          .divider {
            background: #444;
            opacity: 1;
          }
        }
        .mine-main-info {
          margin-top: 13px;

          .mine-state {
            height: 22px;
            margin-bottom: 49px;

            .state-label {
              color: #CCCCCC;
              background-color: inherit;
              border: 1px solid #FFFFFF3D;
            }
          }

          .mine-title {
            min-height: 0;
            gap: 51px;

            .title-wrap {
              color: #fff;
              font-size: 32px;
              font-weight: 700;
              line-height: 120%;
              letter-spacing: -0.48px;
              min-height: 114px;
              display: flex;
              align-items: flex-start;
            }

            .prompt-text {
              color: #fff;
              text-align: right;
              padding-right: 4px;
              font-size: 16px;
              font-weight: 700;
              line-height: 100%;
            }
          }
        }

        .mine-bottom-deco {
          color: #ffd900;
          position: unset;
          margin-top: 30px;

          margin-right: 40px;
          margin-left: auto;
        }

        &.mini {
          .mine-bottom-deco {
            margin: 0;
            position: absolute;
            align-items: flex-end;

            .label {
              color: #fff;
              font-size: 16px;
              font-weight: 700;
              line-height: 100%;
            }

            .desc {
              font-size: 30px;
            }
          }
        }

        &.small-card-title {
          .mine-title {
            .title-wrap {
              font-size: 28px;
              letter-spacing: -0.42px;
            }
          }
        }
      }
    }
  }
  &:not(.reactive) {
    .mine-card-header {
      .mine-title {
        cursor: pointer;
        &:hover {
          background-color: rgba(0, 0, 0, 0.16);
          box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.16);
          border-radius: 4px;
          .title-edit-button {
            display: flex;
          }
        }
      }
    }
  }

  &  > div {
    box-sizing: border-box;
  }
  .error-label {
    width: calc(100% - 16px);
    padding: 4px 5px;
    margin: 8px 8px 0;
    display: flex;
    align-items: center;
    gap: 5px;
    background-color: #F4E43A;
    border-radius: 5px;
    font-size: 12px;
    color: #444;
    z-index: 8;
    i.icon {
      background-color: #fff;
      border-radius: 50%;
    }
  }

  .mine-card-header {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;

    width: 100%;
    height: 30%;
    background-color: $reportColor;
    color: #fff;
    padding: 16px 20px;

    .mine-main-info {
      flex: 1;

      /deep/ .title-input {
        margin-top: -4px;
        pointer-events: auto;
        .label {
          display: none;
        }
        .input-element {
          input {
            height: 32px;
            border-radius: 5px;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.20);
            font-size: 13px;
            line-height: 32px;
            &:focus {
              border-color: transparent;
            }
          }
        }
      }
    }

    .mine-state {
      height: 30px;
      display: inline-flex;
      gap: 8px;
      .icon--new-small {
        position: relative;
        top: 3px;
      }
      .state-label {
        height: 12px;
        display: inline-block;
        color: #EC0047;
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        padding: 4px 6px;
        vertical-align: baseline;
        border-radius: 4px;
        background-color: #fff;
        &.report {
          &:first-letter { text-transform: uppercase; }
        }
        &.compare {
          position: relative;
          display: inline-flex;
          height: 12px;
          padding: 4px 6px 4px 4px;
          justify-content: center;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
          border-radius: 4px;
          outline: 1px solid rgba(255, 255, 255, 0.36);
          background: #EC0047;

          & > span {
            color: #FFF;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 100%;
          }
        }
        &.report-analyze {
          padding: 3px 6px;
          border: 1px solid rgba(0, 0, 0, 0.12);
        }
        &.analyze {
          padding: 3px 6px;
          color: $analyzeColor;
          border: 1px solid rgba(0, 0, 0, 0.12);
          background-color: #fff;;
        }
      }
    }
    .date-view-info {
      display: flex;
      align-items: center;
      gap: 9px;
      opacity: .7;
      margin-bottom: 13px;
      font-size: 12px;
      .view-count-info {
        display: flex;
        align-items: center;

        .icon--views, .icon--views-disabled,.icon--views-content {
          margin-right: 4px;
        }
      }
    }
    .mine-title {
      position: relative;
      color: #fff;
      font-size: 20px;
      font-weight: 600;
      line-height: 1.2em;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.20);
      & span.version {
        position: absolute;
        right: 0;
        bottom: 0;
      }
      & span.title {
        overflow: hidden;
        text-overflow: ellipsis;
        max-height: 2.4em;
        overflow-wrap: break-word;
        max-width: 280px;
        width: 280px;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;

        & .highlight {
          color: #EC0047;
        }
      }
    }
  }

  .footer-status {
    cursor: pointer;
    position: relative;
    width: 100%;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    padding: 10px;
    background-color: #F8F8F8;
    color: #EC0047;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5%;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;

    &.compare-available {
      background-color: #222;
      padding: 0 10px 10px 10px;

      & .ui-btn {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        padding: 0 20px;

        font-size: 16px;
        font-weight: 600;
        &:after,
        &:before {
          border-radius: 4px;
        }
      }
    }

    &:not(.compare-available):before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: rgba(170, 170, 170, 0.1);
      border-top: 1px solid #e7e7e7;
    }
    &:not(.compare-available):hover {
      background-color: #EBEBEB;
    }

    &.analyzing {
      color: #999999;
    }

    &.disabled {
      color: #999999;
      pointer-events: none;
    }

    &.error {
      color: #d7d7d7;
    }
  }

  &.analyzing {
    cursor: default;
    & > div:not(.error-control) {
      pointer-events: none !important;
    }

    .mine-card-header {
      position: relative;
      background-color: #fff;
      .mine-title {
        color: #444;
        text-shadow: none;
      }
      .date-view-info {
        color: rgba(0, 0, 0, 0.44);
        .view-count-info:before {
          border-color: rgba(0, 0, 0, 0.2) !important;
        }
      }
      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: rgba(170, 170, 170, 0.1);
        border-bottom: 1px solid #e7e7e7;
      }
    }
    .mine-in-depth {
      pointer-events: none;
      color: #d7d7d7;
    }
  }

  .mine-card-header {
    .mine-title {
      pointer-events: auto;
      .title-edit-button {
        display: none;
        position: absolute;
        right: 0;
        top: -2px;
        justify-content: center;
        align-items: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: #111;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.20);
        cursor: pointer;
        &:hover {
          background-color: #2F2F2F;
        }
      }
    }
  }
  .retry-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9;
  }
  &.error {
    color: #ccc;
    cursor: default;
    & > div:not(.error-control) {
      pointer-events: none !important;
    }
    .mine-card-header {
      position: relative;
      background-color: #fff !important;
      .date-view-info {
        color: rgba(0, 0, 0, 0.44);
        .view-count-info:before {
          border-color: rgba(0, 0, 0, 0.2) !important;
        }
      }
      .state-label {
        &.report {
          background-color: #ebebeb !important;
          color: #999 !important;
          border: 0;
          padding: 4px 6px;
        }
        &:not(.report):not(.reactive) {
          display: none !important;
        }
      }
      .mine-title {
        color: #ccc !important;
        text-shadow: none;
        &:after {
          color: #000 !important;
        }
        &:hover {
          color: #888 !important;
        }
      }
      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: rgba(170, 170, 170, 0.1);
        border-bottom: 1px solid #e7e7e7;
      }
    }
  }
}

.ellipsis {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  word-break: break-all;
  display: block;

  &--2,
  &--3 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    word-break: break-all;
    overflow-wrap: break-word;
  }

  &--2 {
    -webkit-line-clamp: 2;
  }

  &--3 {
    -webkit-line-clamp: 3;
  }
}

</style>
